import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { useEffect } from 'react';
import {
	MdAccountBalanceWallet,
	MdDirectionsCar,
	MdGroups,
	MdHome,
	MdInsights,
	MdMenu,
	MdOutlineAccountBalanceWallet,
	MdOutlineHub,
	MdOutlineReceiptLong,
	MdOutlineSportsBasketball,
} from 'react-icons/md';
import modules from '@/constants/modules';
import routes from '@/constants/routes';
import { useSidebarStore } from '@/store';
import type { SidebarState } from '@/types';
import './sidebar.styles.css';
import { cn } from '../ui/cn';
import { useOnboardingState } from '@/modules/onboarding/engine/onboarding.hooks';
import { useIsTablet } from '@/common/useIsTablet';

const Sidebar = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const pathname = location.pathname;

	const sidebarStore = useSidebarStore((state: SidebarState) => state);
	const isTablet = useIsTablet();
	// On tablet the sidebar is always collapsed and cannot be expanded.
	const isExpand = isTablet ? false : sidebarStore.isExpand;
	const { stepsQ } = useOnboardingState();
	const isOnboardingFinished = stepsQ.data?.Steps.includes('Finished') ?? false;

	useEffect(() => {
		if (sidebarStore.isHydrated && pathname === routes['/']) {
			navigate(sidebarStore.lastSelectedMenuItem);
		}
	}, [pathname, navigate, sidebarStore.isHydrated, sidebarStore.lastSelectedMenuItem]);

	const itemRenderer = (item: MenuItem) => (
		<div className={cn(
			'p-menuitem-content', pathname === item.url && 'p-menuitem-content-active',
			item.disabled && 'opacity-50 pointer-events-none',
		)}>
			<Link
				to=""
				className="p-menuitem-link"
				onClick={(e) => {
					e.preventDefault();

					if (item?.url) {
						sidebarStore.setLastSelectedMenuItem(item.url);
						navigate(item.url);
					}
				}}
			>
				<div className="p-menuitem-icon">{item.icon}</div>
				<span className="p-menuitem-text">{item.label}</span>
			</Link>
		</div>
	);

	const items: MenuItem[] = [
		{
			id: modules.HOME,
			label: 'Home',
			icon: <MdHome size={24} />,
			url: '/',
			template: itemRenderer,
		},
		// {
		// 	id: modules.TRAVEL_PROGRAM,
		// 	label: !sidebarStore.isExpand ? 'Travel' : 'Travel Program',
		// 	icon: <MdDirectionsCar size={24} />,
		// 	url: '/travel-program/',
		// 	disabled: !isOnboardingFinished,
		// 	template: itemRenderer,
		// },
		{
			id: modules.CONCIERGE,
			label: 'Concierge',
			icon: <MdOutlineHub size={24} />,
			url: '/concierge-start/',
			disabled: !isOnboardingFinished,
			template: itemRenderer,
		},
		{
			id: modules.PEOPLE,
			label: 'People',
			icon: <MdGroups size={24} />,
			url: '/people/',
			disabled: !isOnboardingFinished,
			template: itemRenderer,
		},
		{
			id: modules.INSIGHTS,
			label: 'Insights',
			icon: <MdInsights size={24} />,
			url: '/insights/',
			disabled: !isOnboardingFinished,
			template: itemRenderer,
		},
		// {
		// 	id: modules.PROGRAMS,
		// 	label: 'Programs',
		// 	icon: <MdOutlineSportsBasketball size={24} />,
		// 	url: '/programs/',
		// 	disabled: !isOnboardingFinished,
		// 	template: itemRenderer,
		// },
		{
			id: modules.BILLING,
			label: 'Billing',
			icon: <MdOutlineReceiptLong size={24} />,
			url: '/billing/',
			disabled: !isOnboardingFinished,
			template: itemRenderer,
		},
		{
			id: modules.BUDGET_MANAGEMENT,
			label: !sidebarStore.isExpand ? 'Budget' : 'Budget',
			icon: pathname === '/budget-management/'
				? (<MdAccountBalanceWallet size={24} />)
				: (<MdOutlineAccountBalanceWallet size={24} />),
			url: '/budget-management/',
			disabled: !isOnboardingFinished,
			template: itemRenderer,
		},
	];

	return (
		<div className={`sidebar ${isExpand && 'isExpand'}`}>
			<Button
				className={`${!isExpand && 'isActive'} menu-button tablet:!hidden`}
				onClick={() => sidebarStore.setIsExpand(!sidebarStore.isExpand)}
				icon={<MdMenu size={24} />}
			/>

			<Menu
				model={items}
				className={`custom-menu ${!isExpand && 'shrink'}`}
			/>
		</div>
	);
};

export default Sidebar;
